import { useState, useEffect } from 'react';
import { fetchPatientById, fetchClaimDiagnoses, fetchClaimServiceLines, fetchClaimPayers, submitClaimsToClaimMd, generateClaimMdEdi } from '../services/api';
import { parseLocalDate } from '../utils/dates';
import { printClaimFull } from '../utils/printClaim';
import { claimFormLabel } from '../utils/billing';
import EdiPreviewModal from './EdiPreviewModal';
import './ReviewClaimModal.css';

export default function ReviewClaimModal({ claim, facility, onClose, onSubmitted }) {
  const [patient, setPatient]         = useState(null);
  const [billedPayer, setBilledPayer] = useState(null);
  const [diagnoses, setDiagnoses]     = useState([]);
  const [serviceLines, setServiceLines] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [submitting, setSubmitting]   = useState(false);
  const [generatingEdi, setGeneratingEdi] = useState(false);
  const [ediPreview, setEdiPreview]       = useState(null);
  const [error, setError]             = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const claimId = claim.claim_id ?? claim.id;
        const [diagRes, slRes, payerRes] = await Promise.all([
          fetchClaimDiagnoses(claimId),
          fetchClaimServiceLines(claimId),
          fetchClaimPayers(claimId),
        ]);
        setDiagnoses(diagRes.data || []);
        setServiceLines(slRes.data || []);
        // The payer Claim.MD will transmit this 837 to is the claim's payer_order=1 slot
        // — each COB claim stores its own destination payer there, and fetchClaimPayers
        // already merges claim_payers with the patient's insurance as a fallback.
        setBilledPayer((payerRes.data || []).find((p) => p.payer_order === 1) || null);

        if (claim.patient_id) {
          const patRes = await fetchPatientById(claim.patient_id);
          setPatient(patRes.data?.patient || patRes.data || null);
        }
      } catch {
        // non-fatal — show what we have
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [claim]);

  const handlePrint = () =>
    printClaimFull(claim.claim_id ?? claim.id, facility);

  const handleGenerateEdi = async () => {
    setGeneratingEdi(true);
    setError(null);
    try {
      const claimId = claim.claim_id ?? claim.id;
      console.log('[EDI] generating for claimId:', claimId);
      const res = await generateClaimMdEdi([claimId]);
      console.log('[EDI] response:', res);
      const files = res?.files || [];
      console.log('[EDI] files count:', files.length);
      if (files.length > 0) setEdiPreview(files);
      else console.warn('[EDI] files array is empty — modal will not open');
    } catch (err) {
      console.error('[EDI] caught error:', err);
      setError(err.response?.data?.error || 'Failed to generate EDI file.');
    } finally {
      setGeneratingEdi(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      // Transmit the 837 to the clearinghouse. The server validates 837 readiness
      // first, then uploads and records the per-claim acknowledgement.
      const claimId = claim.claim_id ?? claim.id;
      const res = await submitClaimsToClaimMd([claimId]);
      const batches = res?.batches || [];
      const rejectedClaims = batches.flatMap((b) =>
        (b.claims || []).filter((c) => /^(R|E)/i.test(c.status || ''))
      );

      if (rejectedClaims.length > 0) {
        // Clearinghouse rejected the upload — keep the modal open so the biller
        // can read the reason, correct the claim, and resubmit.
        setError(
          'Claim.MD rejected this claim:\n'
          + rejectedClaims.map((c) => `• ${c.message || 'Rejected by clearinghouse'}`).join('\n')
        );
        setSubmitting(false);
        return;
      }

      // Accepted into the clearinghouse pipeline (claim is now Pending).
      onSubmitted();
    } catch (err) {
      // 837 readiness validation runs before anything is transmitted.
      const ve = err?.response?.data?.validationErrors;
      if (ve) {
        setError(
          'Cannot submit — fix these before sending:\n'
          + ve.map((e) => `• ${e.errors.join('; ')}`).join('\n')
        );
      } else {
        setError(
          err?.response?.data?.message
          || err?.response?.data?.error
          || 'Failed to submit claim to Claim.MD. Please try again.'
        );
      }
      setSubmitting(false);
    }
  };

  const fmt = (n) => '$' + parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
  const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <>
    <div className="rcm-backdrop">
      <div className="rcm-modal">

        {/* Header */}
        <div className="rcm-header">
          <div className="rcm-header-left">
            <div className="rcm-header-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
              </svg>
            </div>
            <div>
              <h2 className="rcm-title">Review Claim</h2>
              <p className="rcm-subtitle">{claim.claim_number} &middot; {facility?.name || 'Unknown Facility'}</p>
            </div>
          </div>
          <button className="rcm-close" onClick={onClose} disabled={submitting}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="rcm-body">

          {/* Claim info */}
          <div className="rcm-section">
            <p className="rcm-section-title">Claim Information</p>
            <div className="rcm-info-grid">
              <div className="rcm-info-item">
                <span className="rcm-info-label">Claim Number</span>
                <span className="rcm-info-value">{claim.claim_number}</span>
              </div>
              <div className="rcm-info-item">
                <span className="rcm-info-label">Patient</span>
                <span className="rcm-info-value">
                  {patient ? `${patient.first_name} ${patient.last_name}` : claim.patient_name}
                </span>
              </div>
              <div className="rcm-info-item">
                <span className="rcm-info-label">Provider</span>
                <span className="rcm-info-value">{claim.provider || claim.rendering_provider || '—'}</span>
              </div>
              <div className="rcm-info-item">
                <span className="rcm-info-label">Service Date</span>
                <span className="rcm-info-value">{fmtDate(claim.service_date)}</span>
              </div>
              <div className="rcm-info-item">
                <span className="rcm-info-label">Total Amount</span>
                <span className="rcm-info-value rcm-amount">{fmt(claim.amount)}</span>
              </div>
              <div className="rcm-info-item">
                <span className="rcm-info-label">Claim Form</span>
                <span className="rcm-info-value">
                  {claimFormLabel(claim.form_type, claim.billing_component)}
                </span>
              </div>
              {claim.encounter_id && (
                <div className="rcm-info-item">
                  <span className="rcm-info-label">Encounter ID</span>
                  <span className="rcm-info-value">{claim.encounter_id}</span>
                </div>
              )}
              {claim.referring_provider && (
                <div className="rcm-info-item">
                  <span className="rcm-info-label">Referring Provider</span>
                  <span className="rcm-info-value">{claim.referring_provider}</span>
                </div>
              )}
            </div>
          </div>

          {/* CPT codes */}
          {claim.cpt_codes?.length > 0 && (
            <div className="rcm-section">
              <p className="rcm-section-title">CPT / HCPCS Codes</p>
              <div className="rcm-tag-list">
                {claim.cpt_codes.map((c) => (
                  <span key={c} className="rcm-tag">{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Diagnoses */}
          {!loading && diagnoses.length > 0 && (
            <div className="rcm-section">
              <p className="rcm-section-title">Diagnoses</p>
              <div className="rcm-tag-list">
                {[...diagnoses].sort((a, b) => a.sequence - b.sequence).map((d) => (
                  <span key={d.id ?? d.sequence} className="rcm-tag rcm-tag--dx">
                    <span className="rcm-tag-seq">{String.fromCharCode(64 + d.sequence)}</span>
                    {d.icd_code}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Service lines */}
          {!loading && serviceLines.length > 0 && (
            <div className="rcm-section">
              <p className="rcm-section-title">Service Lines</p>
              <div className="rcm-sl-table-wrap">
                <table className="rcm-sl-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>CPT / HCPCS</th>
                      <th>POS</th>
                      <th>Units</th>
                      <th>Charges</th>
                      <th>DX Ptrs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {serviceLines.map((sl, i) => (
                      <tr key={sl.id ?? i}>
                        <td>
                          {sl.service_date_from
                            ? parseLocalDate(sl.service_date_from).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'}
                        </td>
                        <td><span className="rcm-tag rcm-tag--sm">{sl.cpt_hcpcs_code}</span></td>
                        <td>{sl.place_of_service || '—'}</td>
                        <td>{sl.units || '1'}</td>
                        <td>{sl.charges ? fmt(sl.charges) : '—'}</td>
                        <td>{sl.diagnosis_pointers || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {loading && <div className="rcm-loading">Loading claim details…</div>}

          {/* Billing To — the single payer this claim will be transmitted to via Claim.MD,
              matching the destination payer the 837 is built for (claim_payers payer_order=1).
              We intentionally show only the billed payer here, not the patient's full COB
              ladder, so the biller sees exactly who this submission goes to. */}
          {billedPayer && (
            <div className="rcm-section">
              <p className="rcm-section-title">Billing To</p>
              <div className="rcm-ins-list">
                <div className="rcm-ins-row">
                  <span className="rcm-ins-order">
                    {['Primary', 'Secondary', 'Tertiary'][(claim.cob_order || 1) - 1] ?? `Payer ${claim.cob_order}`}
                  </span>
                  <span className="rcm-ins-name">{billedPayer.payer_name || '—'}</span>
                  <span className="rcm-ins-id">{billedPayer.insured_id || billedPayer.policy_number || '—'}</span>
                </div>
              </div>
            </div>
          )}

          {error && <div className="rcm-error" style={{ whiteSpace: 'pre-line' }}>{error}</div>}
        </div>

        {/* Footer */}
        <div className="rcm-footer">
          <div className="rcm-footer-left">
            <button className="rcm-btn-print" onClick={handlePrint}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 6 2 18 2 18 9" />
                <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                <rect x="6" y="14" width="12" height="8" />
              </svg>
              Print
            </button>
            <button className="rcm-btn-edi" onClick={handleGenerateEdi} disabled={generatingEdi || submitting}>
              {generatingEdi ? (
                <><span className="rcm-spinner" />Generating…</>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Generate .edi
                </>
              )}
            </button>
          </div>
          <div className="rcm-footer-right">
            <button className="rcm-btn-cancel" onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button className="rcm-btn-submit" onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <><span className="rcm-spinner" />Submitting…</>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22 4 12 14.01 9 11.01" />
                  </svg>
                  Submit to Claim.MD
                </>
              )}
            </button>
          </div>
        </div>

      </div>
    </div>

    {ediPreview && (
      <EdiPreviewModal
        files={ediPreview}
        onClose={() => setEdiPreview(null)}
      />
    )}
    </>
  );
}
